import { useState, type FormEvent } from 'react';
import { Mail, Lock, AlertCircle, KeyRound } from 'lucide-react';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/card';
import ThemeToggle from '@/components/ThemeToggle';
import { useAuth } from '@/contexts/AuthContext';

type LoginStep = 'email' | 'otp' | 'password';

export default function LoginPage() {
  const { requestLoginOtp, verifyLoginOtp, login } = useAuth();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loginChallenge, setLoginChallenge] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const handleRequestOtp = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await requestLoginOtp(email);
      setStep('otp');
      setOtp('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a verification code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await verifyLoginOtp(email, otp);
      setLoginChallenge(result.loginChallenge);
      setStep('password');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(email, password, loginChallenge);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setOtp('');
    setLoginChallenge('');
    setPassword('');
    setError('');
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-3 py-6 sm:px-4 sm:py-10">
      <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
        <ThemeToggle className="w-auto" />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <img src="/logo.png" alt="68M Holidays" className="mx-auto h-14 w-auto sm:h-16" />
          <CardTitle className="text-xl sm:text-2xl">68M Holidays</CardTitle>
          <p className="text-sm text-muted-foreground">
            {step === 'email'
              ? 'Enter your email to receive a verification code.'
              : step === 'otp'
                ? 'Enter the 6-digit code we sent to your email.'
                : 'Enter your password to finish signing in.'}
          </p>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className="pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              {error ? (
                <div
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Sending code…' : 'Send verification code'}
              </Button>
            </form>
          ) : null}

          {step === 'otp' ? (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                Code sent to <span className="font-medium text-foreground">{email}</span>
              </p>
              <div className="space-y-2">
                <label htmlFor="otp" className="text-sm font-medium text-foreground">
                  Verification code
                </label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit code"
                    className="pl-9 tracking-widest"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    minLength={6}
                    maxLength={6}
                    disabled={submitting}
                  />
                </div>
              </div>

              {error ? (
                <div
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting || otp.length !== 6}>
                {submitting ? 'Checking code…' : 'Continue'}
              </Button>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={submitting}
                  onClick={() => {
                    void (async () => {
                      setError('');
                      setSubmitting(true);
                      try {
                        await requestLoginOtp(email);
                      } catch (err) {
                        setError(
                          err instanceof Error
                            ? err.message
                            : 'Could not send a new code. Please try again.'
                        );
                      } finally {
                        setSubmitting(false);
                      }
                    })();
                  }}
                >
                  Resend code
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={backToEmail}>
                  Change email
                </Button>
              </div>
            </form>
          ) : null}

          {step === 'password' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                Signed in as <span className="font-medium text-foreground">{email}</span>
              </p>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="pl-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={submitting}
                    autoFocus
                  />
                </div>
              </div>

              {error ? (
                <div
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={backToEmail}>
                Start over
              </Button>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
